import { plainToInstance } from "class-transformer"
import { validate } from "class-validator"
import moment from "moment-timezone"
import * as PapaParse from "papaparse"

import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config.ts"
import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import {
    PolicyModality,
    PolicyModalityCode,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import type { IIndexable } from "@common-shared/types/general.types.ts"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types.ts"
import type { IPayer } from "@common-shared/types/responseData/commonResponseData.types.ts"
import { IOneVerifyPracticeRole } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { FRONT_LOOKUP_CONFIG } from "@front-shared/config/lookup.config"
import { FRONT_REGEX_CONFIG } from "@front-shared/config/regex.config.ts"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import { LookupService } from "@front-shared/services/lookup.service.ts"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types.ts"

import { COVERAGE_CONFIG } from "../config/coverage.config"
import { CSV_CHECKER_CONFIG } from "../config/csvChecker.config"
import { GENERAL_CONFIG } from "../config/general.config"
import { POLICIES_COLUMNS_CONFIG } from "../config/policiesColumns.config"
import {
    RawCsvDiscoverInputDTO,
    RawCsvGeneralInputDTO,
    RawCsvMedicaidInputDTO,
    RawCsvSmartScanInputDTO,
} from "../dto/rawCsvInput.dto"
import type {
    ICSVUploadValidationError,
    IDynamicCheckerFormInputData,
} from "../types/data.types"
import type { IUploadCsvFieldMapping } from "../types/objects.types"
import { ExportReportHelper } from "./exportReport.helper"
import { FeatureFlagsHelper } from "./featureFlags.helper"
import { UtilHelper } from "./util.helper"

export class CsvHelper {
    /**
     * Get single column for preview CSV based on mapping
     */
    static getSingleColumnUploadPreview(
        mapping: IUploadCsvFieldMapping,
        inputData: IDynamicCheckerFormInputData,
    ): any {
        const emptyColumnValue = mapping.formatting?.convertOnEmptyToUnknown
            ? COVERAGE_CONFIG.defaultFallbackCoverageDataValueUnknown
            : undefined

        let columnValue = CsvHelper.getSingleExportColumnValue(
            mapping.valuePath,
            inputData,
            emptyColumnValue,
        )

        // Process special formatting for columns, like money or date formatting
        // Also check if it has some value
        if (mapping.formatting && columnValue !== emptyColumnValue) {
            columnValue = ExportReportHelper.applyColumnFormatting(
                columnValue,
                mapping.formatting,
                emptyColumnValue,
                false,
            )
        }

        // Always convert boolean to 1 | 0
        if (typeof columnValue === "boolean") {
            columnValue = +columnValue
        }

        return columnValue
    }

    /**
     * Get coverage checker download columns
     */
    static getCoverageCheckerDownloadCSVColumns(
        neededModalities = [PolicyModality.MENTAL_HEALTH],
        csvCheckType: QuotaUsageType,
    ): IPolicyColumnConfiguration[] {
        let neededColumns = [
            ...POLICIES_COLUMNS_CONFIG.checkerDownloadResultsColumns[
                csvCheckType
            ],
        ]

        const resolvedFeatureFlags = BrowserStorageHelper.get<FeatureFlag[]>(
            GENERAL_CONFIG.browserStorageKeys.enabledFeatureFlags,
            undefined,
            BrowserStorageType.sessionStorage,
        )

        const hasEarlyReleaseFeaturesEnabled =
            FeatureFlagsHelper.isFeatureFlagEnabled(
                resolvedFeatureFlags || [],
                FeatureFlag.ONE_VERIFY_EARLY_RELEASE_FEATURES,
            )

        if (!hasEarlyReleaseFeaturesEnabled) {
            neededColumns = neededColumns.filter(
                item =>
                    !CSV_CHECKER_CONFIG.earlyReleaseItems.columns.includes(
                        item.columnKey,
                    ),
            )
        }

        const additionalColumns =
            ExportReportHelper.getModalitiesSpecificColumnsConfigurations(
                neededModalities,
            )

        // Process additional columns that require specific order
        for (const column of additionalColumns) {
            const overrideOrder = column.listTypeOrder?.checkerCsvDownload

            if (typeof overrideOrder === "undefined") {
                continue
            }

            neededColumns.splice(overrideOrder, 0, column)
        }

        const additionalColumnsWithoutOrder = additionalColumns.filter(
            item =>
                typeof item.listTypeOrder?.checkerCsvDownload === "undefined",
        )

        // Will insert new columns without order to the end
        const result = [...neededColumns, ...additionalColumnsWithoutOrder]

        return csvCheckType === QuotaUsageType.MEDICAID
            ? result.filter(item => !item.isMedicaidCheckerExportIgnored)
            : result
    }

    /**
     * Download a CSV document
     */
    static createAndDownloadCsvDocument(
        data: string,
        fileName = "download",
        addTimeSuffix = true,
    ) {
        let url = ""

        try {
            const blob = new Blob([data], {
                type: "text/csv",
            })

            url = URL.createObjectURL(blob)

            const downloadLink = document.createElement("a")

            downloadLink.href = url
            downloadLink.download =
                fileName +
                (addTimeSuffix
                    ? `-${moment().format(GENERAL_CONFIG.defaultMomentDateTimeFormat)}`
                    : "") +
                ".csv"

            downloadLink.click()
        } finally {
            URL.revokeObjectURL(url)
        }
    }

    /**
     * Get data parsed from uploaded CSV for continuous monitoring coverage checker
     * Handles STANDARD, DISCOVER and SMART SCAN CSV types
     */
    static async processUploadedContinuousMonitoringCsv(
        csv: PapaParse.ParseResult<unknown>,
        availableCptCodesByModalities: Partial<
            Record<PolicyModality, ISelectRenderedOption[]>
        >,
        csvCheckType: QuotaUsageType,
        selectedPracticeRole?: IOneVerifyPracticeRole,
        maxBulkCoverageChecks = CSV_CHECKER_CONFIG.maxBulkCoverageChecksCSV,
    ): Promise<IDynamicCheckerFormInputData[]> {
        let foundPayers: IPayer[] = []

        const processedInputData: IDynamicCheckerFormInputData[] = []

        for (const [index, row] of csv.data.entries()) {
            const rawRow = row as IIndexable

            // +2 since it's header + code index starts from 0
            const id = (index + 2).toString()

            try {
                if (processedInputData.length >= maxBulkCoverageChecks) {
                    break
                }

                let typedRow:
                    | RawCsvSmartScanInputDTO
                    | RawCsvDiscoverInputDTO
                    | RawCsvGeneralInputDTO

                switch (csvCheckType) {
                    case QuotaUsageType.SMART_SCAN:
                        typedRow = plainToInstance(RawCsvSmartScanInputDTO, row)

                        break
                    case QuotaUsageType.DISCOVER:
                        typedRow = plainToInstance(RawCsvDiscoverInputDTO, row)

                        break
                    default:
                        typedRow = plainToInstance(RawCsvGeneralInputDTO, row)
                }

                let validationErrors: ICSVUploadValidationError[] = (
                    await validate(typedRow)
                ).map(item => {
                    return {
                        field: item.property as keyof IDynamicCheckerFormInputData,
                        errors: item.constraints
                            ? Object.values(item.constraints)
                            : [item.toString()],
                    }
                })

                const commonData = CsvHelper.processUploadedCommonCsvRow(
                    typedRow,
                    csvCheckType,
                    availableCptCodesByModalities,
                    selectedPracticeRole,
                    rawRow,
                )

                if (commonData.validationErrors?.length) {
                    validationErrors = [
                        ...validationErrors,
                        ...commonData.validationErrors,
                    ]
                }

                let payer: IPayer | undefined

                // Process payer if it exists and required by CSV type
                // Try to find it in lookup
                const rowPayer =
                    typedRow instanceof RawCsvSmartScanInputDTO
                        ? undefined
                        : typedRow.payer.trim()

                if (rowPayer) {
                    const alreadyFound = foundPayers.find(
                        item => item.payerId === rowPayer,
                    )

                    if (alreadyFound) {
                        payer = alreadyFound
                    } else {
                        const lookupPayerResult =
                            await LookupService.lookupPayers(
                                FRONT_LOOKUP_CONFIG.payerIdCorrectionsMapping[
                                    rowPayer
                                ] || rowPayer,
                            )

                        if (lookupPayerResult.length) {
                            payer = lookupPayerResult[0]

                            // Save found payer for the future
                            // In case next row will need same payer object - we won't need to go to API
                            foundPayers = [...foundPayers, lookupPayerResult[0]]
                        }

                        if (!payer) {
                            validationErrors.push({
                                field: "payer",
                                errors: ["Incorrect payer ID"],
                            })
                        }
                    }
                }

                // Check if payer is supported for Discover
                if (
                    csvCheckType === QuotaUsageType.DISCOVER &&
                    payer &&
                    !payer.isDiscoverSupported
                ) {
                    validationErrors.push({
                        field: "payer",
                        errors: ["Payer is not supported for Discover"],
                    })
                }

                const rowInputData: IDynamicCheckerFormInputData = {
                    id,
                    modalityCode: commonData.modalityCode,

                    csvValidationErrors: validationErrors,

                    providerNpi:
                        typedRow.providerNpi?.length &&
                        FRONT_REGEX_CONFIG.npi.test(typedRow.providerNpi)
                            ? typedRow.providerNpi
                            : selectedPracticeRole?.practice.groupNPI || "",

                    memberId:
                        typedRow instanceof RawCsvGeneralInputDTO
                            ? typedRow.memberId || undefined
                            : undefined,

                    zipCode:
                        typedRow instanceof RawCsvGeneralInputDTO
                            ? undefined
                            : typedRow.zipCode || undefined,

                    firstName: typedRow.firstName,
                    lastName: typedRow.lastName,

                    dob: commonData.dob || "",

                    payer,

                    // Leave as is in $
                    sessionRate: typedRow.sessionRate?.replace("$", ""),
                    cptCode: commonData.cptCode,

                    inNetworkCheck:
                        typedRow.inNetworkCheck !==
                        PayerCoverageCheckNetwork.OUT,

                    outNetworkCheck:
                        typedRow.inNetworkCheck ===
                            PayerCoverageCheckNetwork.OUT ||
                        typedRow.inNetworkCheck ===
                            PayerCoverageCheckNetwork.IN_OUT,

                    officeId: typedRow.officeId || undefined,

                    customerPatientType:
                        typedRow.customerPatientType || undefined,

                    customerPatientId: typedRow.customerPatientId || undefined,

                    customerPatientNextAppointmentDate:
                        commonData.nextAppointmentDate,

                    passThroughColumns: commonData.passThroughColumns,

                    isBulk: true,
                }

                // Divide in and out checks into different rows
                // It will also make IDs for these different rows uniques
                if (rowInputData.inNetworkCheck) {
                    processedInputData.push({
                        ...rowInputData,

                        id: rowInputData.outNetworkCheck
                            ? `${rowInputData.id}_IN`
                            : rowInputData.id,

                        inNetworkCheck: true,
                        outNetworkCheck: false,
                    })
                }

                if (rowInputData.outNetworkCheck) {
                    processedInputData.push({
                        ...rowInputData,

                        id: rowInputData.inNetworkCheck
                            ? `${rowInputData.id}_OUT`
                            : rowInputData.id,

                        inNetworkCheck: false,
                        outNetworkCheck: true,
                    })
                }
            } catch (e) {
                processedInputData.push({
                    id,
                    csvValidationErrors: [
                        {
                            field: "id",
                            errors: [
                                `Something went wrong during processing CSV row. ${
                                    e instanceof Error
                                        ? `Error message: ${e.message}.`
                                        : ""
                                } `,
                            ],
                        },
                    ],
                } as IDynamicCheckerFormInputData)
            }
        }

        return CsvHelper.getSortedCsvUploadInputData<IDynamicCheckerFormInputData>(
            processedInputData,
        )
    }

    /**
     * Get data parsed from uploaded CSV of type Medicaid
     */
    static async processUploadedMedicaidCsv(
        csv: PapaParse.ParseResult<unknown>,
        availableCptCodesByModalities: Partial<
            Record<PolicyModality, ISelectRenderedOption[]>
        >,
        csvCheckType: QuotaUsageType,
        selectedPracticeRole?: IOneVerifyPracticeRole,
        maxBulkCoverageChecks = CSV_CHECKER_CONFIG.maxBulkCoverageChecksCSV,
    ): Promise<IDynamicCheckerFormInputData[]> {
        const processedInputData: IDynamicCheckerFormInputData[] = []

        for (const [index, row] of csv.data.entries()) {
            const rawRow = row as Record<string, string>

            // +2 since it's header + code index starts from 0
            const id = (index + 2).toString()

            try {
                if (processedInputData.length >= maxBulkCoverageChecks) {
                    break
                }

                const typedRow = plainToInstance(RawCsvMedicaidInputDTO, row)

                let validationErrors: ICSVUploadValidationError[] = (
                    await validate(typedRow)
                ).map(item => {
                    return {
                        field: item.property as keyof IDynamicCheckerFormInputData,
                        errors: item.constraints
                            ? Object.values(item.constraints)
                            : [item.toString()],
                    }
                })

                const commonData = CsvHelper.processUploadedCommonCsvRow(
                    typedRow,
                    csvCheckType,
                    availableCptCodesByModalities,
                    selectedPracticeRole,
                    rawRow,
                )

                if (commonData.validationErrors?.length) {
                    validationErrors = [
                        ...validationErrors,
                        ...commonData.validationErrors,
                    ]
                }

                // Check if state requires member ID
                if (
                    COVERAGE_CONFIG.medicaidStatesRequireMemberID.includes(
                        typedRow.state,
                    ) &&
                    !typedRow.memberId
                ) {
                    validationErrors.push({
                        field: "memberId",
                        errors: [
                            `Member ID is required for state ${typedRow.state}`,
                        ],
                    })
                } else if (
                    // Check if state requires member ID or gender
                    COVERAGE_CONFIG.medicaidStatesRequireGenderOrMemberID.includes(
                        typedRow.state,
                    ) &&
                    !typedRow.memberId &&
                    !typedRow.memberGender
                ) {
                    validationErrors.push({
                        field: "memberId",
                        errors: [
                            `Member ID or Member Gender is required for state ${typedRow.state}`,
                        ],
                    })
                } else if (
                    !COVERAGE_CONFIG.medicaidStatesSupported.includes(
                        typedRow.state,
                    )
                ) {
                    validationErrors.push({
                        field: "state",
                        errors: [
                            `Medicaid checks are not supported for state ${typedRow.state}`,
                        ],
                    })
                }

                const rowInputData: IDynamicCheckerFormInputData = {
                    id,
                    modalityCode: commonData.modalityCode,

                    providerNpi:
                        typedRow.providerNpi?.length &&
                        FRONT_REGEX_CONFIG.npi.test(typedRow.providerNpi)
                            ? typedRow.providerNpi
                            : selectedPracticeRole?.practice.groupNPI || "",

                    state: typedRow.state,
                    memberId: typedRow.memberId || undefined,
                    memberGender: typedRow.memberGender || undefined,

                    dob: commonData.dob || "",

                    firstName: typedRow.firstName,
                    lastName: typedRow.lastName,

                    cptCode: commonData.cptCode,

                    customerPatientType:
                        typedRow.customerPatientType || undefined,

                    customerPatientId: typedRow.customerPatientId || undefined,

                    customerPatientNextAppointmentDate:
                        commonData.nextAppointmentDate,

                    officeId: typedRow.officeId || undefined,

                    csvValidationErrors: validationErrors,

                    passThroughColumns: commonData.passThroughColumns,

                    isBulk: true,
                }

                processedInputData.push(rowInputData)
            } catch (e) {
                processedInputData.push({
                    id,
                    csvValidationErrors: [
                        {
                            field: "id",
                            errors: [
                                `Something went wrong during processing CSV row. ${
                                    e instanceof Error
                                        ? `Error message: ${e.message}.`
                                        : ""
                                } `,
                            ],
                        },
                    ],
                } as IDynamicCheckerFormInputData)
            }
        }

        return CsvHelper.getSortedCsvUploadInputData<IDynamicCheckerFormInputData>(
            processedInputData,
        )
    }

    /**
     * Process uploaded common CSV row
     */
    private static processUploadedCommonCsvRow(
        row: {
            modalityCode: PolicyModalityCode
            cptCode?: string
            dob: string
            customerPatientNextAppointmentDate?: string
        },
        csvCheckType: QuotaUsageType,
        availableCptCodesByModalities: Partial<
            Record<PolicyModality, ISelectRenderedOption[]>
        >,
        selectedPracticeRole?: IOneVerifyPracticeRole,
        rawRow?: Record<string, string>,
    ): {
        validationErrors?: ICSVUploadValidationError[]
        modalityCode: PolicyModalityCode
        cptCode?: string
        dob?: string
        nextAppointmentDate?: string
        passThroughColumns: Record<string, string>
    } {
        const validationErrors: ICSVUploadValidationError[] = []

        // Check if modality code is valid and practice supports it
        if (
            !Object.values(PolicyModalityCode).includes(row.modalityCode) ||
            (selectedPracticeRole?.availableModalityCodes?.length &&
                !selectedPracticeRole.availableModalityCodes.includes(
                    row.modalityCode,
                ))
        ) {
            validationErrors.push({
                field: "modalityCode",
                errors: ["Not supported service"],
            })
        }

        const cptCode: string | undefined = row.cptCode

        // Check CPT code to use
        if (cptCode) {
            const cptCodeErrors: string[] = []

            try {
                const supportedCptCodes = Object.values(
                    availableCptCodesByModalities,
                )
                    .flat()
                    .map(item => item.value)

                if (!supportedCptCodes.includes(cptCode)) {
                    cptCodeErrors.push("Not supported CPT code")
                }

                // Check combination of modality code and cpt code
                const modality =
                    COVERAGE_CONFIG.modalityCodeToModalityMapping[
                        row.modalityCode
                    ]

                const modalityCptCodes = modality
                    ? Object.values(
                          availableCptCodesByModalities[modality] || {},
                      )?.map(item => item.value)
                    : []

                if (!modalityCptCodes.includes(cptCode)) {
                    cptCodeErrors.push(
                        "Wrong combination of service and CPT code",
                    )
                }
            } catch (e) {
                console.error(e, {
                    cptCode,
                })

                cptCodeErrors.push("Failed to validate CPT code")
            }

            if (cptCodeErrors.length) {
                validationErrors.push({
                    field: "cptCode",
                    errors: cptCodeErrors,
                })
            }
        }

        const { valid: dobToUse } = UtilHelper.getPossibleDateField(row.dob)

        if (row.dob) {
            if (!dobToUse) {
                validationErrors.push({
                    field: "dob",
                    errors: [
                        "Format of DOB is wrong. Should be one of: MM/DD/YYYY or MM/DD/YY or YYYY-MM-DD.",
                    ],
                })
            } else if (dobToUse?.isSameOrAfter(moment().startOf("day"))) {
                validationErrors.push({
                    field: "dob",
                    errors: ["DOB should be more than today."],
                })
            }
        }

        const dob = dobToUse?.isValid()
            ? UtilHelper.dateToMysqlFormat(dobToUse.toDate())
            : undefined

        if (!dob && dobToUse) {
            validationErrors.push({
                field: "dob",
                errors: ["DOB in CSV is in wrong format."],
            })
        }

        const { valid: nextAppointmentDateToUse } =
            UtilHelper.getPossibleDateField(
                row.customerPatientNextAppointmentDate,
            )

        if (
            nextAppointmentDateToUse?.isSameOrBefore(
                moment().subtract(1, "day").startOf("day"),
            )
        ) {
            validationErrors.push({
                field: "customerPatientNextAppointmentDate",
                errors: ["Next appointment date should be in the future."],
            })
        }

        const nextAppointmentDate = nextAppointmentDateToUse?.isValid()
            ? UtilHelper.dateToMysqlFormat(nextAppointmentDateToUse.toDate())
            : undefined

        const passThroughColumns: Record<string, string> = {}

        const resultDownloadExtraHeaders =
            CsvHelper.getCoverageCheckerDownloadCSVColumns(
                selectedPracticeRole?.availableModalities,
                csvCheckType,
            ).map(item => item.label)

        const resolvedFeatureFlags = BrowserStorageHelper.get<FeatureFlag[]>(
            GENERAL_CONFIG.browserStorageKeys.enabledFeatureFlags,
            undefined,
            BrowserStorageType.sessionStorage,
        )

        const hasEarlyReleaseFeaturesEnabled =
            FeatureFlagsHelper.isFeatureFlagEnabled(
                resolvedFeatureFlags || [],
                FeatureFlag.ONE_VERIFY_EARLY_RELEASE_FEATURES,
            )

        // Process extra columns that are just pass through
        // We'd need to go through "row" keys - it preserves order
        // And see which ones are extra (compare to CHECKER_CONFIG.uploadCsvHeaders index value)
        // And just add them to inputData.passThroughColumns
        // Then during export they will be appended to end of csv
        // Also make sure not to add headers that are part of our output - in case they reused previously downloaded csv
        Object.keys(rawRow || {}).forEach((key, index) => {
            const headerKeys = CSV_CHECKER_CONFIG.uploadCsvHeaderKeys[
                csvCheckType
            ].filter(
                key =>
                    !CSV_CHECKER_CONFIG.earlyReleaseItems.fields.includes(
                        key,
                    ) || hasEarlyReleaseFeaturesEnabled,
            )

            if (
                index < headerKeys.length ||
                resultDownloadExtraHeaders.includes(key) ||
                !rawRow
            ) {
                return
            }

            passThroughColumns[key] = rawRow[key]
        })

        return {
            validationErrors,
            modalityCode: row.modalityCode,
            cptCode: cptCode || undefined,
            dob,
            nextAppointmentDate,
            passThroughColumns,
        }
    }

    /**
     * Get data parsed from uploaded CSV sorted by amount of errors in row
     */
    private static getSortedCsvUploadInputData<
        Type extends { csvValidationErrors?: ICSVUploadValidationError[] },
    >(data: Type[]): Type[] {
        const dataCopy = [...data]

        dataCopy.sort((x, y) => {
            const xHasErrors = !!x.csvValidationErrors?.length
            const yHasErrors = !!y.csvValidationErrors?.length

            return xHasErrors === yHasErrors ? 0 : xHasErrors ? -1 : 1
        })

        return dataCopy
    }

    /**
     * Read a nested value from row data using a dot-separated path.
     */
    static getValueByPath(
        inputData: object | null | undefined,
        valuePath: string,
        emptyColumnValue?: string,
    ): unknown {
        let columnValue: unknown = inputData

        valuePath.split(".").forEach(pathPart => {
            try {
                columnValue = (columnValue as Record<string, unknown>)?.[
                    pathPart
                ]
            } catch {
                columnValue = emptyColumnValue
            }
        })

        if (typeof columnValue === "undefined" || columnValue === null) {
            columnValue = emptyColumnValue
        }

        return columnValue
    }

    /**
     * Get single column for download CSV based on config
     */
    private static getSingleExportColumnValue(
        valuePath: keyof IDynamicCheckerFormInputData | string,
        inputData: IDynamicCheckerFormInputData,
        emptyColumnValue?: string,
    ): any {
        return CsvHelper.getValueByPath(
            inputData,
            valuePath,
            emptyColumnValue,
        )
    }
}
